import { type LetterFileDocumentDto } from "@/app/dto/letter-file-document.dto";
import { useWopiApi } from "@/app/http/wopi.api";
import { useLetterFileStore } from "@/app/stores/letter-file.store";
import { type ParapheurConfig } from "@/app/types";
import { type Nullable, type StrictNullable, useConfig } from "@pasta/box-v2";
import type { Dictionary } from "lodash";
import { defineStore } from "pinia";

export interface WopiStoreState {
  enabled: boolean;
  // K2000 vars
  k2000Enabled: boolean;
  k2000Editables: Dictionary<string>;

  // Collabora vars
  collaboraEnabled: boolean;
  collaboraEditables: Dictionary<string>;
  currentDocId?: Nullable<number>;
  currentUrl?: Nullable<string>;
  currentToken?: Nullable<string>;
  iFrameLoaded: boolean;
  trustedUrl: StrictNullable<string>;
  debugLocalIp: StrictNullable<string>;
  frameLoadTimeout: StrictNullable<number>;
}

export const useWopiStore = defineStore("wopi", {
  state: (): WopiStoreState => {
    return {
      enabled: false,

      // K2000 vars
      k2000Enabled: false,
      k2000Editables: {},

      // Collabora vars
      collaboraEnabled: false,
      collaboraEditables: {},
      currentDocId: null,
      currentUrl: null,
      currentToken: null,
      iFrameLoaded: false,
      trustedUrl: null,
      debugLocalIp: null,
      frameLoadTimeout: 10_000,
    };
  },
  actions: {
    async getToken(id: number) {
      return useWopiApi().getToken(id);
    },
    async fetchCollaboraEditables() {
      if (this.enabled) {
        this.setCollaboraEditables(await useWopiApi().getCollaboraEditables());
      }
    },
    async editWithCollabora(document: LetterFileDocumentDto) {
      this.currentDocId = document.id;
      const wopiSrc = encodeURIComponent(`${this.config.apiBaseUrl}/wopi/files/${document.id}`);
      this.currentUrl = `${
        this.collaboraEditables[document.extension.slice(1)]
      }WOPISrc=${wopiSrc}&closebutton=true&lang=fr`;
      if (this.currentUrl.includes("localhost")) {
        if (!this.debugLocalIp) {
          console.log("Please configure wopi.debugLocalIp to use locally");
          return;
        }
        // localhost hack, replace the ip here with yours when locally working with collabora
        this.currentUrl = this.currentUrl.replace(/localhost/g, this.debugLocalIp);
      }
      this.currentToken = await this.getToken(document.id);
    },
    async editWithK2000(document: LetterFileDocumentDto) {
      const { link } = await useWopiApi().fetchLink(document.id);
      window.open(link, "_blank");
    },
    setEnabled(value: boolean) {
      this.enabled = value;
    },
    setK2000Enabled(value: boolean) {
      this.k2000Enabled = value;
    },
    setK2000Editables(editables: Dictionary<string>) {
      this.k2000Editables = editables;
    },
    setCollaboraEnabled(value: boolean) {
      this.collaboraEnabled = value;
    },
    setCollaboraEditables(editables: Dictionary<string>) {
      this.collaboraEditables = editables;
    },
    setIFrameLoaded(value: boolean) {
      this.iFrameLoaded = value;
    },
    setTrustedUrl(value: StrictNullable<string>) {
      this.trustedUrl = value;
    },
    setDebugLocalIp(value: StrictNullable<string>) {
      this.debugLocalIp = value;
    },
    setFrameLoadTimeout(value: StrictNullable<number>) {
      this.frameLoadTimeout = value;
    },
    async close() {
      this.setIFrameLoaded(false);
      this.currentDocId = null;
      this.currentToken = null;
      this.currentUrl = null;
      await useLetterFileStore().fetchCurrent();
    },
  },
  getters: {
    config() {
      return useConfig<ParapheurConfig>();
    },
  },
});
